import type { MetadataRegistryEntry } from '@compass/client/types/metadata_registry';
import { metadataRegistryService } from '@services/api';
import { useQuery } from '@tanstack/react-query';

import { pickMetadataFilterDefinitions } from './types';

// A single page (200) is plenty for a per-org advanced-filter set — the registry
// only holds a handful of searchable evaluation keys per org.
const METADATA_FILTER_DEFINITIONS_LIMIT = 200;

const queryKeys = {
  metadataFilterDefinitions: {
    all: ['compass', 'metadataFilterDefinitions'] as const,
    byOrganization: (organizationId: string) =>
      [...queryKeys.metadataFilterDefinitions.all, 'byOrganization', organizationId] as const,
  },
};

const EMPTY_ENTRIES: MetadataRegistryEntry[] = [];

/**
 * Fetches the org's searchable evaluation metadata-registry entries and keeps
 * only those in METADATA_FILTER_KEYS, in that order — the advanced-filter
 * definitions rendered on BOTH the QC list and analytics pages (AQ-496).
 */
export const useMetadataFilterDefinitions = (organizationId?: string | null) => {
  const query = useQuery({
    queryKey: queryKeys.metadataFilterDefinitions.byOrganization(organizationId || ''),
    queryFn: async () => {
      if (!organizationId) {
        return EMPTY_ENTRIES;
      }

      const response = await metadataRegistryService.listMetadataRegistryEntries({
        organization_id: organizationId,
        domain: 'evaluation',
        searchable: true,
        limit: METADATA_FILTER_DEFINITIONS_LIMIT,
      });

      return pickMetadataFilterDefinitions(response.entries ?? EMPTY_ENTRIES);
    },
    enabled: !!organizationId,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });

  return {
    definitions: query.data ?? EMPTY_ENTRIES,
    isLoading: query.isLoading,
    error: query.error,
  };
};
